import React, { useState, useEffect } from 'react';
import { logFailedImage } from '../lib/imageLogger';
import { getOptimalImageUrl, getFallbackImageUrl } from '../utils/formatImageUrls';

interface CardImageProps {
  src: string;
  alt: string;
  className?: string;
  cardId: string;
  width?: number;
  quality?: number;
  style?: React.CSSProperties;
  onClick?: () => void;
}

const CardImage: React.FC<CardImageProps> = ({
  src,
  alt,
  className = '',
  cardId,
  width = 245,
  quality = 75,
  style,
  onClick
}) => {
  const [imgError, setImgError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  
  // Maximum number of retries
  const MAX_RETRIES = 3;
  
  // Placeholder image URL for fallbacks
  const PLACEHOLDER_URL = 'https://yjzlookljahphgxcwpnv.supabase.co/storage/v1/object/public/pokemon-images/placeholder.png';
  
  // Get optimized WebP URL with fallback chain
  const [currentSrc, setCurrentSrc] = useState(() => 
    getOptimalImageUrl(src, { width, quality })
  );

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const img = e.target as HTMLImageElement;
    
    if (retryCount < MAX_RETRIES) {
      // Get next fallback URL
      const fallbackUrl = getFallbackImageUrl(img.src);
      
      if (fallbackUrl !== img.src) {
        console.log(`[CardImage] Retrying with fallback URL for ${cardId}:`, fallbackUrl);
        setRetryCount(prev => prev + 1);
        setCurrentSrc(fallbackUrl);
        return;
      }
    }
    
    // If we've exhausted retries, log the error and use placeholder
    if (!imgError) {
      logFailedImage(cardId, img.src);
      setImgError(true);
      setCurrentSrc(PLACEHOLDER_URL);
      console.log('[CardImage] Using placeholder for', cardId);
    }
  };

  // Update currentSrc when src prop changes
  useEffect(() => {
    setCurrentSrc(getOptimalImageUrl(src, { width, quality }));
    setImgError(false);
    setRetryCount(0);
  }, [src, width, quality]);

  // Convert the React prop to lowercase for HTML attribute
  const imgProps: any = {
    src: currentSrc,
    alt,
    className,
    loading: "eager",
    decoding: "sync",
    onError: handleImageError,
    style,
    onClick
  };

  return <img {...imgProps} />;
};

export default React.memo(CardImage);